import React from 'react';
import { TIMELINE_CONFIG } from '../../utils/constants';
import { CardData } from '../../types';

interface TimeGridProps {
  /** Se o grid está ativo */
  active: boolean;
  /** Escala atual do canvas (zoom) */
  scale?: number;
  /** Posição de pan do canvas */
  pan?: { x: number; y: number };
  /** Cards para desenhar linhas correspondentes */
  cards?: CardData[];
}

/**
 * Componente TimeGrid - Grade de tempo visual (Gráfico de Gantt)
 * 
 * Desenha linhas verticais apenas onde há cards, com a largura correspondente a cada card
 */
export default function TimeGrid({ 
  active, 
  scale = 1, 
  pan = { x: 0, y: 0 },
  cards = [],
}: TimeGridProps) {
  if (!active) return null;

  // Usar pan e scale das props (sistema do VsmCanvas)
  const x = pan.x;
  const y = pan.y;
  const zoom = scale;
  
  const lines: React.ReactNode[] = [];

  // Filtrar apenas cards de atividade que têm largura calculada
  // Ordenar por posição X para garantir ordem correta
  const cardsToDraw = cards
    .filter(card => {
      // Apenas cards de atividade com calculatedWidth
      return card.tipo === 'atividade' && 
             card.calculatedWidth && 
             card.calculatedWidth > 0;
    })
    .sort((a, b) => a.posicao.x - b.posicao.x);

  // Desenhar uma linha no início de cada card
  cardsToDraw.forEach((card, index) => {
    const cardX = card.posicao.x;
    const cardWidth = card.calculatedWidth || TIMELINE_CONFIG.BASE_WIDTH;
    
    // Calcular tempo para label (baseado na posição X)
    const totalMinutes = cardX / TIMELINE_CONFIG.PIXELS_PER_MINUTE;
    const hours = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);
    
    // Desenhar linha na posição inicial do card
    lines.push(
      <g key={`line-${card.id}`} transform={`translate(${cardX}, 0)`}>
        {/* Linha vertical na posição inicial do card */}
        <line x1="0" y1="0" x2="0" y2="99999" stroke="#E2E8F0" strokeWidth="1" />
        {/* Label de tempo */}
        {cardX >= 0 && (
          <text x="6" y="15" className="text-[10px] font-bold fill-slate-400 font-mono">
            {hours}h{mins > 0 ? `${mins}m` : ''}
          </text>
        )}
      </g>
    );
    
    // Desenhar linha no fim do último card (para fechar a visualização)
    if (index === cardsToDraw.length - 1) {
      const cardEndX = cardX + cardWidth;
      lines.push(
        <g key={`line-end-${card.id}`} transform={`translate(${cardEndX}, 0)`}>
          {/* Linha vertical na posição final do último card */}
          <line x1="0" y1="0" x2="0" y2="99999" stroke="#E2E8F0" strokeWidth="1" />
        </g>
      );
    }
  });

  return (
    <div className="absolute inset-0 pointer-events-none z-0">
      <svg className="w-full h-full" style={{ transform: `translate(${x}px, ${y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
        {lines}
      </svg>
    </div>
  );
}
